const STORAGE_KEY = "themix_volume";

const audio = new Audio();
audio.preload = "auto";
let unlocked = false;

const listeners = new Set<(volume: number) => void>();

function clamp(v: number): number {
  return Math.min(1, Math.max(0, v));
}

function loadVolume(): number {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw == null) return 0.8;
    const n = Number(raw);
    return Number.isFinite(n) ? clamp(n) : 0.8;
  } catch {
    return 0.8;
  }
}

audio.volume = loadVolume();

function notify(): void {
  for (const fn of listeners) fn(audio.volume);
}

export function getVolume(): number {
  return audio.volume;
}

export function setVolume(value: number): void {
  audio.volume = clamp(value);
  try {
    localStorage.setItem(STORAGE_KEY, String(audio.volume));
  } catch {
    /* ignore */
  }
  notify();
}

export function subscribeVolume(fn: (volume: number) => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

export async function unlockAudio(): Promise<void> {
  unlocked = true;
  try {
    audio.src = "data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAESsAACJWAAACABAAZGF0YQAAAAA=";
    await audio.play();
    audio.pause();
  } catch {
    /* gesture required */
  }
}

export function isAudioUnlocked(): boolean {
  return unlocked;
}

let playGeneration = 0;

export function playClip(url: string, start: number): Promise<void> {
  const gen = ++playGeneration;
  audio.pause();
  audio.volume = getVolume();
  return new Promise((resolve, reject) => {
    let started = false;
    const go = () => {
      if (started || gen !== playGeneration) return;
      started = true;
      try {
        if (start > 0 && Number.isFinite(start)) audio.currentTime = start;
      } catch {
        /* media not seekable yet */
      }
      void audio
        .play()
        .then(() => {
          unlocked = true;
          resolve();
        })
        .catch((err: unknown) => {
          unlocked = false;
          reject(err instanceof Error ? err : new Error("Lecture bloquée"));
        });
    };
    audio.addEventListener("loadedmetadata", go, { once: true });
    audio.addEventListener(
      "error",
      () => {
        if (gen !== playGeneration || started) return;
        reject(new Error("Extrait illisible"));
      },
      { once: true },
    );
    audio.src = url;
    audio.load();
    if (audio.readyState >= 1) go();
  });
}

export function pauseClip(): void {
  audio.pause();
}

export function resumeClip(): void {
  audio.volume = getVolume();
  void audio.play().catch(() => {
    unlocked = false;
  });
}

export function stopClip(): void {
  audio.pause();
  audio.removeAttribute("src");
}

export function setClipTime(seconds: number): void {
  try {
    audio.currentTime = seconds;
  } catch {
    /* ignore */
  }
}
